/* PDP variant picker styling - the Rubik swatch app.

   WHY JAVASCRIPT FOR SOMETHING PURELY COSMETIC:
   the picker the shopper uses is <rubik-swatch>, a web component that renders
   into a SHADOW ROOT. Theme stylesheets cannot cross that boundary, so the only
   way to restyle it from the theme side is to put a <style> element inside the
   shadow root. That is all this does.

   Worth knowing before editing: the theme ALSO ships its own <variant-picker>
   for this product, and an app hides it with an injected
   variant-picker { display: none !important; }. That is correct - Rubik
   replaces it. An earlier attempt forced the theme's picker visible on the
   assumption it was the one on screen, which put two model dropdowns and two
   colour grids on the PDP. Leave it hidden.

   The observer is here because the component re-renders its own shadow content
   on variant change, which drops anything appended to it. */
(function () {
  'use strict';

  /* One vertical grid, after the Mous PDP:

       hairline rule
       20px
       LABEL
       10px
       control
       20px
       hairline rule

     THE SEPARATORS ARE ON THE LEGEND, NOT THE FIELDSET. Putting border-top on
     the fieldset is the obvious move and it silently draws nothing: a browser
     cuts a gap in a fieldset's top border to make room for its <legend>, and
     this legend is full width, so the gap is the entire line. Computed style
     reported a 1px border the whole time - it simply was not painted. The
     legend is an ordinary box with no such behaviour, so the rule lives there.
     A border-BOTTOM on the fieldset is not affected, so the closing line under
     the last group can stay where it is.

     The 20px under the rule is the other thing that fights back. The legend
     sits flush against the fieldset's top edge and ignores BOTH the fieldset's
     padding-top and its own margin-top - each was tried and measured 0px, with
     the label sitting on the rule. Padding on the legend is what works, because
     that grows the legend's own box downward.

     Everything AFTER the legend does take the fieldset's padding-top, and it
     adds to the legend's margin-bottom - that pair produced a 26px
     label-to-control gap when 8px was intended. So padding-top stays 0 and the
     gap to the control is the legend's margin-bottom alone. */
  var CSS = [
    '.rubik-swatch__option {',
    '  border: 0 !important;',
    '  margin: 0 !important;',
    '  padding: 0 0 20px !important;',
    '}',
    /* Bottom borders are not interrupted by the legend, so this one paints. */
    '.rubik-swatch__option:last-of-type {',
    '  border-bottom: 1px solid rgba(0, 0, 0, 0.12) !important;',
    '}',

    /* Flex with baseline alignment, not the default inline flow. Both labels
       are renamed by collapsing the app\'s text to font-size 0 and painting the
       replacement from ::before, and a zero-sized inline box carries no usable
       baseline - inline, "STYLE:" sat 13px below the value beside it. As flex
       items each box takes its baseline from the text actually rendered. */
    '.rubik-swatch__option-name {',
    '  box-sizing: border-box !important;',
    '  display: flex !important;',
    '  align-items: baseline !important;',
    '  flex-wrap: wrap !important;',
    '  gap: 5px !important;',
    '  width: 100% !important;',
    '  border-top: 1px solid rgba(0, 0, 0, 0.12) !important;',
    '  margin: 0 0 10px !important;',
    '  padding-top: 20px !important;',
    '  font-size: 13px !important;',
    '  line-height: 1.3 !important;',
    '}',

    /* Anything that is not the legend is the control. Zeroing its top margin
       keeps the 10px gap identical in both groups - the swatch grid ships with
       a margin of its own, the select does not. */
    '.rubik-swatch__option > *:not(legend) { margin-top: 0 !important; }',

    '.rubik-swatch__option-name-label {',
    '  font-weight: 700 !important;',
    '  text-transform: uppercase !important;',
    '  letter-spacing: 0.06em !important;',
    '}',
    '.rubik-swatch__option-name-value {',
    '  margin-left: 0 !important;',
    '  font-weight: 400 !important;',
    '  text-transform: none !important;',
    '  letter-spacing: 0 !important;',
    '}',

    /* The option name comes from the product\'s option names in Shopify, so the
       durable fix for either word is to rename the option there - this override
       only changes what is painted. */

    /* Model group: "DEVICE", and the repeated value dropped since the select
       underneath already shows it. */
    '.rubik-swatch__option:has(.rubik-swatch__dropdown) .rubik-swatch__option-name-value,',
    '.rubik-swatch__option:has(.rubik-swatch__dropdown) .rubik-swatch__option-colon {',
    '  display: none !important;',
    '}',
    '.rubik-swatch__option:has(.rubik-swatch__dropdown) .rubik-swatch__option-name-label {',
    '  font-size: 0 !important;',
    '}',
    '.rubik-swatch__option:has(.rubik-swatch__dropdown) .rubik-swatch__option-name-label::before {',
    '  content: "Device";',
    '  font-size: 13px;',
    '  line-height: 1.3;',
    '  font-weight: 700;',
    '  text-transform: uppercase;',
    '  letter-spacing: 0.06em;',
    '}',

    /* Swatch group: "STYLE:" in place of "COLOR:". The colon is part of the
       replacement text because the app\'s own colon span collapses with the
       rest of the label. */
    '.rubik-swatch__option:not(:has(.rubik-swatch__dropdown)) .rubik-swatch__option-name-label {',
    '  font-size: 0 !important;',
    '}',
    '.rubik-swatch__option:not(:has(.rubik-swatch__dropdown)) .rubik-swatch__option-name-label::before {',
    '  content: "Style:";',
    '  font-size: 13px;',
    '  line-height: 1.3;',
    '  font-weight: 700;',
    '  text-transform: uppercase;',
    '  letter-spacing: 0.06em;',
    '}',

    /* Border and weight only. Padding and background are left alone on purpose:
       the chevron is a background image on this select and resetting either one
       wipes it. */
    '.rubik-swatch__dropdown {',
    '  border: 1.5px solid #1a1a1a !important;',
    '  border-radius: 10px !important;',
    '  font-size: 16px !important;',
    '  font-weight: 600 !important;',
    '  color: #1a1a1a !important;',
    '}'
  ].join('\n');

  function inject(host) {
    var sr = host.shadowRoot;
    if (!sr || sr.querySelector('style[data-suti-picker]')) return;
    var s = document.createElement('style');
    s.setAttribute('data-suti-picker', '');
    s.textContent = CSS;
    sr.appendChild(s);
  }

  function scan() {
    var hosts = document.querySelectorAll('rubik-swatch');
    for (var i = 0; i < hosts.length; i++) {
      var h = hosts[i];
      if (!h.shadowRoot) continue;
      inject(h);
      if (!h.__sutiPickerObserved) {
        h.__sutiPickerObserved = true;
        (function (host) {
          new MutationObserver(function () { inject(host); })
            .observe(host.shadowRoot, { childList: true });
        })(h);
      }
    }
  }

  scan();

  /* The component upgrades after this script runs, so poll briefly rather than
     racing it. Stops after 20s; the observer covers everything after that. */
  var tries = 0;
  var timer = setInterval(function () {
    scan();
    if (++tries > 40) clearInterval(timer);
  }, 500);

  document.addEventListener('DOMContentLoaded', scan);
})();
